import { useState, useEffect, useCallback } from 'react'
import { cn } from '@/lib/utils'
import { leaderboardApi, type LeaderboardEntry, type ApiResponse } from '@/lib/api'
import { RankItem } from '@/components/rank-item'
import { useAuth } from '@/hooks/use-auth'
import { Card, CardContent } from '@/components/ui/card'
import { Trophy, Flame, RefreshCw, TrendingUp } from 'lucide-react'

type Period = 'week' | 'month' | 'year'
type BoardType = 'duration' | 'streak'

const periodLabels: Record<Period, string> = { week: '本周', month: '本月', year: '本年' }

export function LeaderboardPage() {
  const { user } = useAuth()
  const [period, setPeriod] = useState<Period>('week')
  const [boardType, setBoardType] = useState<BoardType>('streak')
  const [entries, setEntries] = useState<LeaderboardEntry[]>([])
  const [myEntry, setMyEntry] = useState<LeaderboardEntry | null>(null)
  const [maxValue, setMaxValue] = useState(0)
  const [loading, setLoading] = useState(false)

  const fetchBoard = useCallback(async () => {
    setLoading(true)
    try {
      if (boardType === 'duration') {
        const res = await leaderboardApi.duration(period)
        console.log('Duration API response:', res)
        const data = res as unknown as ApiResponse<{ entries: LeaderboardEntry[]; myEntry: LeaderboardEntry | null; maxDuration: number }>
        console.log('Duration entries:', data.data.entries)
        setEntries(data.data.entries)
        setMyEntry(data.data.myEntry)
        setMaxValue(data.data.maxDuration)
      } else {
        const res = await leaderboardApi.streak()
        console.log('Streak API response:', res)
        const data = res as unknown as ApiResponse<{ entries: LeaderboardEntry[]; myEntry: LeaderboardEntry | null; maxStreak: number }>
        console.log('Streak entries:', data.data.entries)
        setEntries(data.data.entries)
        setMyEntry(data.data.myEntry)
        setMaxValue(data.data.maxStreak)
      }
    } catch (e) {
      console.error('Failed to fetch leaderboard:', e)
      console.error('Error details:', e)
    } finally {
      setLoading(false)
    }
  }, [period, boardType])

  useEffect(() => {
    fetchBoard()
  }, [fetchBoard])

  return (
    <div className="flex flex-col min-h-[calc(100dvh-3.5rem)]">
      {/* Header */}
      <div className="px-5 pt-4 pb-3 safe-top">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold">排行榜</h2>
          <button
            onClick={fetchBoard}
            disabled={loading}
            className="p-1.5 hover:bg-muted rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={cn('h-4 w-4 text-muted-foreground', loading && 'animate-spin')} />
          </button>
        </div>

        {/* Board type toggle */}
        <div className="flex gap-2 p-1 bg-muted/50 rounded-xl mb-3">
          <button
            onClick={() => setBoardType('duration')}
            className={cn(
              'flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium rounded-lg transition-all',
              boardType === 'duration' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'
            )}
          >
            <TrendingUp className="h-3.5 w-3.5" />
            时长榜
          </button>
          <button
            onClick={() => setBoardType('streak')}
            className={cn(
              'flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium rounded-lg transition-all',
              boardType === 'streak' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'
            )}
          >
            <Flame className="h-3.5 w-3.5" />
            坚持榜
          </button>
        </div>

        {/* Period toggle (duration only) */}
        {boardType === 'duration' && (
          <div className="flex gap-2">
            {(['week', 'month', 'year'] as Period[]).map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={cn(
                  'flex-1 py-1.5 text-xs font-medium rounded-lg transition-all',
                  period === p ? 'bg-primary text-primary-foreground' : 'bg-muted/50 text-muted-foreground'
                )}
              >
                {periodLabels[p]}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-5 pb-24">
        {loading && !entries.length ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : entries.length === 0 && !myEntry ? (
          <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
            <Trophy className="h-12 w-12 mb-3 opacity-30" />
            <p className="text-sm">暂无排行数据</p>
            <p className="text-xs mt-1">开始记录来争夺榜首吧</p>
          </div>
        ) : (
          <>
            <Card>
              <CardContent className="p-2">
                {entries.map((entry) => (
                  <RankItem
                    key={entry.user_id}
                    rank={entry.rank}
                    nickname={entry.nickname}
                    value={entry.value}
                    unit={boardType === 'duration' ? '秒' : '天'}
                    maxValue={maxValue}
                    isSelf={entry.is_self}
                  />
                ))}
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* My rank fixed bottom */}
      {myEntry && !entries.some(e => e.is_self) && (
        <div className="fixed bottom-14 left-0 right-0 px-5 pb-2 z-30 safe-bottom">
          <Card className="border-primary/30 bg-primary/5">
            <CardContent className="p-3">
              <RankItem
                rank={myEntry.rank}
                nickname={user?.nickname || '我'}
                value={myEntry.value}
                unit={boardType === 'duration' ? '秒' : '天'}
                maxValue={maxValue}
                isSelf
              />
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
